import { Button, Col, Divider, Form, Input, message, Row, Select, Spin, Typography } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import api from '../../../services/api';
import { Container } from '../styles';
import { ButtonGroup, FormLabel, PreviewContainer, PreviewImage, StyledCard, StyledFormItem, SubTitle, TitleBar } from './styles';

const { Title } = Typography;

export function PrintLabels() {
  const profile = useSelector((state) => state.user.profile);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [cosmeticScreen, setCosmeticScreen] = useState([]);
  const [otherPorts, setOtherPorts] = useState([]);
  const [printers, setPrinters] = useState([]);
  const userPrinterStorageKey = React.useMemo(() => {
    const userIdentifier = profile?.id || profile?.email || 'anonymous';
    return `printLabels.selectedPrinterIp.${userIdentifier}`;
  }, [profile]);

  const persistSelectedPrinter = React.useCallback(
    (printerIp) => {
      if (!printerIp) return;
      try {
        localStorage.setItem(userPrinterStorageKey, printerIp);
      } catch (error) {
        // Local storage can fail in restricted browser contexts.
      }
    },
    [userPrinterStorageKey]
  );

  // Fetch cosmeticScreen and otherPorts options on mount
  React.useEffect(() => {
    async function fetchOptions() {
      try {
        const [cosmeticScreenData, otherPortsData, printersData] = await Promise.all([
          api.get('cosmetic_screen_options'),
          api.get('other_ports_options'),
          api.get('params/show/byService?service=Printer'),
        ]);
        setCosmeticScreen(cosmeticScreenData.data || []);
        setOtherPorts(otherPortsData.data || []);

        const availablePrinters = (printersData.data || []).filter((printer) => printer.value_param);
        setPrinters(availablePrinters);

        let savedPrinterIp = null;
        try {
          savedPrinterIp = localStorage.getItem(userPrinterStorageKey);
        } catch (error) {
          savedPrinterIp = null;
        }

        const savedPrinterIsAvailable = availablePrinters.some((printer) => printer.value_param === savedPrinterIp);
        if (availablePrinters.length > 0) {
          const defaultPrinterIp = savedPrinterIsAvailable ? savedPrinterIp : availablePrinters[0].value_param;
          form.setFieldsValue({ selectedPrinterIp: defaultPrinterIp });
          persistSelectedPrinter(defaultPrinterIp);
        }
      } catch (err) {
        message.error('Failed to load label options');
      }
    }
    fetchOptions();
  }, [form, persistSelectedPrinter, userPrinterStorageKey]);

  // Helper: assign keyboard type
  function assignKBType(keyboard_type_id) {
    switch (keyboard_type_id) {
      case 1:
        return 'RUBBER EN';
      case 2:
        return 'RUBBER CA-FR';
      case 3:
        return 'STANDARD EN';
      case 4:
        return 'STANDARD CA-FR';
      case 5:
        return 'EMISSIVE EN';
      case 6:
        return 'EMISSIVE CA-FR';
      default:
        return '';
    }
  }

  // Build label info from API data
  async function getLabelInfo(serialNumberInput) {
    try {
      const response = await api.post('assetDiagnostic/showBySerialNumber', {
        serialNumber: serialNumberInput,
      });
      if (!response.data) {
        message.error('Serial Number not found');
        return null;
      }
      const unitInfo = response.data;
      const labelInfo = {
        grade: '',
        location: '',
        partNumber: '',
        serialNumber: serialNumberInput,
        storage: '',
        storage2: '',
        ram: '',
        ram2: '',
        operatingSystem: '',
        cpu: '',
        description: '',
        technician: '',
        diagnostic: [],
      };
      labelInfo.partNumber = unitInfo.inventory_items?.item.part_number;
      labelInfo.description = unitInfo.inventory_items.description || '';
      labelInfo.technician = unitInfo.user?.name || '';
      labelInfo.cpu = unitInfo.inventory_items?.cpu?.name || unitInfo.inventory_items?.item?.cpu?.name || '';
      labelInfo.operatingSystem =
        unitInfo.function_operating_system !== 'na' && unitInfo.function_operating_system !== null ? unitInfo.inventory_items.operating_system : '';
      labelInfo.storage = unitInfo.function_storage !== 'na' && unitInfo.function_storage !== null ? unitInfo.inventory_items.storage : '';
      labelInfo.storage2 = unitInfo.function_storage_2 !== 'na' && unitInfo.function_storage_2 !== null ? unitInfo.inventory_items.storage_2 : '';
      labelInfo.ram = unitInfo.function_ram !== 'na' && unitInfo.function_ram !== null ? unitInfo.inventory_items.ram : '';
      labelInfo.ram2 = unitInfo.function_ram_2 !== 'na' && unitInfo.function_ram_2 !== null ? unitInfo.inventory_items.ram_2 : '';
      // Grade logic
      if (labelInfo.grade === '') {
        switch (unitInfo.company_grade) {
          case 'A':
            labelInfo.grade = 'A';
            break;
          case 'B':
            labelInfo.grade = 'B';
            break;
          case 'C':
            labelInfo.grade = 'C';
            break;
          case 'D':
            labelInfo.grade = 'D';
            break;
          case 'NA':
          case 'na':
            labelInfo.grade = 'NA';
            break;
          default:
            break;
        }
      }
      if (unitInfo.inventory_items.status === 'Failed') labelInfo.grade = 'FA';
      switch (unitInfo.inventory_items.condition) {
        case 'New':
          labelInfo.grade = 'N';
          break;
        case 'Open Box':
          labelInfo.grade = 'OB';
          break;
        case 'ToBeInspected':
          labelInfo.grade = 'TBI';
          break;
        case 'For Parts':
          labelInfo.grade = 'FP';
          break;
        default:
          break;
      }
      if (unitInfo.inventory_items.item.subcategory_id === 4 && unitInfo.inventory_items.condition !== 'New') {
        labelInfo.grade = `${unitInfo.inventory_items.battery}%`;
      }
      // Diagnostics (partial, main ones)
      if (unitInfo.function_screen === 'passed' && unitInfo.inventory_items.touch !== 'non_touch') labelInfo.diagnostic.push('TOUCHSCREEN');
      if (unitInfo.function_screen === 'failed' && unitInfo.inventory_items.touch !== 'non_touch') labelInfo.diagnostic.push('failedTOUCHSCREEN');
      if (unitInfo.function_screen === 'passed' && unitInfo.inventory_items.touch === 'non_touch') labelInfo.diagnostic.push('NON-TOUCHSCREEN');
      if (unitInfo.function_screen === 'failed' && unitInfo.inventory_items.touch === 'non_touch') labelInfo.diagnostic.push('failedNON-TOUCHSCREEN');
      if (unitInfo.function_lcd_screen === 'failed') labelInfo.diagnostic.push('failedLCD');
      if (unitInfo.cosmetics_screen === 'passed' && unitInfo.inventory_items.cosmetic_screen) {
        const cosmetic_screen = unitInfo.inventory_items.cosmetic_screen.split(',').map(Number);
        cosmetic_screen.forEach((screen) => {
          const screenExists = cosmeticScreen.find((cs) => cs.id === screen);
          if (screenExists) labelInfo.diagnostic.push(screenExists.name);
        });
      }
      if (unitInfo.inventory_items.hours) labelInfo.diagnostic.push(`HOURS: ${unitInfo.inventory_items.hours}`);
      if (unitInfo.function_gps === 'passed') labelInfo.diagnostic.push('GPS');
      else if (unitInfo.function_gps === 'failed') labelInfo.diagnostic.push('failedGPS');
      if (unitInfo.function_graphics_card === 'passed') labelInfo.diagnostic.push(unitInfo.inventory_items.graphics_card);
      else if (unitInfo.function_graphics_card === 'failed') labelInfo.diagnostic.push('failedGRAPHICS CARD');
      if (unitInfo.function_dedicated_gpu === 'passed') labelInfo.diagnostic.push(unitInfo.inventory_items.dedicated_gpu_model);
      else if (unitInfo.function_dedicated_gpu === 'failed') labelInfo.diagnostic.push('failedDEDICATED GPU');
      if (unitInfo.function_battery === 'passed' || unitInfo.inventory_items.battery)
        labelInfo.diagnostic.push(`BATTERY: ${unitInfo.inventory_items.battery}%`);
      else if (unitInfo.function_battery === 'failed') labelInfo.diagnostic.push('BATTERY: failed');
      if (unitInfo.function_battery2 === 'passed' || unitInfo.inventory_items.battery2)
        labelInfo.diagnostic.push(`REMOVABLE BATT: ${unitInfo.inventory_items.battery2}%`);
      else if (unitInfo.function_battery2 === 'failed') labelInfo.diagnostic.push('REMOVABLE BATTERY: failed');
      if (unitInfo.function_battery3 === 'passed' || unitInfo.inventory_items.battery3)
        labelInfo.diagnostic.push(`REMOVABLE BATT 2: ${unitInfo.inventory_items.battery3}%`);
      else if (unitInfo.function_battery3 === 'failed') labelInfo.diagnostic.push('REMOVABLE BATTERY 2: failed');
      if (unitInfo.cosmetics_stylus === 'passed') labelInfo.diagnostic.push('STYLUS');
      else if (unitInfo.cosmetics_stylus === 'failed') labelInfo.diagnostic.push('failedSTYLUS');
      if (unitInfo.function_mousepad === 'passed') labelInfo.diagnostic.push('MOUSE PAD');
      else if (unitInfo.function_mousepad === 'failed') labelInfo.diagnostic.push('failedMOUSE PAD');
      if (unitInfo.function_usb === 'passed') labelInfo.diagnostic.push('USB');
      else if (unitInfo.function_usb === 'failed') labelInfo.diagnostic.push('failedUSB');
      if (unitInfo.function_wlan === 'passed') labelInfo.diagnostic.push('WLAN');
      else if (unitInfo.function_wlan === 'failed') labelInfo.diagnostic.push('failedWLAN');
      if (unitInfo.function_wwan === 'passed') labelInfo.diagnostic.push('WWAN');
      else if (unitInfo.function_wwan === 'failed') labelInfo.diagnostic.push('failedWWAN');
      if (unitInfo.function_bluetooth === 'passed') labelInfo.diagnostic.push('BLUETOOTH');
      else if (unitInfo.function_bluetooth === 'failed') labelInfo.diagnostic.push('failedBLUETOOTH');
      if (unitInfo.function_audio === 'passed') labelInfo.diagnostic.push('AUDIO');
      else if (unitInfo.function_audio === 'failed') labelInfo.diagnostic.push('failedAUDIO');
      if (unitInfo.function_keyboard_ports === 'passed' && unitInfo.inventory_items.keyboard_ports) {
        const other_ports = unitInfo.inventory_items.keyboard_ports.split(',').map(Number);
        other_ports.forEach((port) => {
          const portExists = otherPorts.find((op) => op.id === port);
          if (portExists) labelInfo.diagnostic.push(portExists.name);
        });
      } else if (unitInfo.function_keyboard_ports === 'failed') labelInfo.diagnostic.push('failedOTHER PORTS');
      if (unitInfo.function_camera === 'passed') labelInfo.diagnostic.push('CAMERA');
      else if (unitInfo.function_camera === 'failed') labelInfo.diagnostic.push('failedCAMERA');
      if (unitInfo.function_smartcard === 'passed') labelInfo.diagnostic.push(`${unitInfo.inventory_items?.smart_card_type?.toUpperCase() || ''} SMARTCARD`);
      else if (unitInfo.function_smartcard === 'failed') labelInfo.diagnostic.push('failedSMARTCARD');
      if (unitInfo.function_fingerprint === 'passed') labelInfo.diagnostic.push('FINGER PRINT');
      else if (unitInfo.function_fingerprint === 'failed') labelInfo.diagnostic.push('failedFINGER PRINT');
      if (unitInfo.function_barcode_reader === 'passed') labelInfo.diagnostic.push('BARCODE READER');
      else if (unitInfo.function_barcode_reader === 'failed') labelInfo.diagnostic.push('failedBARCODE READER');
      if (unitInfo.function_keyboard === 'passed') labelInfo.diagnostic.push(`KB ${assignKBType(unitInfo.inventory_items.keyboard_type_id)}`);
      else if (unitInfo.function_keyboard === 'failed') labelInfo.diagnostic.push('failedKEYBOARD');
      if (unitInfo.function_optical_drive === 'passed') labelInfo.diagnostic.push('OPTICAL DRIVE');
      else if (unitInfo.function_optical_drive === 'failed') labelInfo.diagnostic.push('failedOPTICAL DRIVE');
      if (unitInfo.function_outdoor_viewable === 'yes') labelInfo.diagnostic.push('OUTDOOR VIEWABLE');
      if (unitInfo.function_nits === 'passed') labelInfo.diagnostic.push(`${unitInfo.inventory_items?.nits} NITS`);
      else if (unitInfo.function_nits === 'failed') labelInfo.diagnostic.push('failedNits');
      if (unitInfo.cosmetics_cracks === 'unfixable') {
        labelInfo.diagnostic.push('UNFIXABLE');
        if (unitInfo.inventory_items.condition !== 'For Parts') labelInfo.grade = 'D*';
      }
      return labelInfo;
    } catch (error) {
      message.error('Error fetching label info');
      return null;
    }
  }

  // Build ZPL from label info
  function buildZPL(labelInfo) {
    const gradeOrBattery = labelInfo.grade.includes('%') ? 'BATTERY' : 'GRADE';
    const labelSkeleton =
      `^XA` +
      `^FX------------ Label border` +
      `^FO5,5^GB600,400,4^FS` +
      `^FX ------------ box border for specs` +
      `^FO5,60^GB480,280,4^FS` +
      `^FX ------------ box border for grade, location, etc` +
      `^FO481,5^GB124,400,4^FS` +
      `^FX ------------ horizontal line below grade` +
      `^FO482,133^GB120,3,3^FS` +
      `^FX ------------ horizontal line below location` +
      `^FO482,266^GB120,3,3^FS` +
      `^FX ------------ horizontal line below CPU` +
      `^FO8,113^GB475,2,2^FS` +
      `^FX ------------ vertical line in the specs box` +
      `^FO245,115^GB1,225,1^FS` +
      `^FX ------------ GRADE LABEL` +
      `^FO490,15^CF0,20^FD${gradeOrBattery}^FS` +
      `^FX ------------ LOCATION LABEL` +
      `^FO490,142^CF0,20^FD^FS`;
    const partNumber = `^FO12,11^CF0,28^FDP/N:${labelInfo.partNumber}^FS`;
    const serialNumber = `^FO12,37^CF0,28^FDS/N:${labelInfo.serialNumber}^FS`;
    const storageRam = `^FO15,70^CF0,20^FD${labelInfo.storage}-${labelInfo.storage2}-${labelInfo.ram}-${labelInfo.ram2}^FS`;
    const cpuOs = `^FO15,95^CF0,20^FD${labelInfo.cpu}-${labelInfo.operatingSystem}^FS`;
    const gradeFontSize = labelInfo.grade.includes('%') ? 50 : 100;
    const grade = `^FB145,1,0,C^FO470,45^CF0,${gradeFontSize}^FD${labelInfo.grade}\\&^FS`;
    const qrCode = `^FB175,1,0,C^FO505,265^BQN,2,3^FDMM,AP/N:${labelInfo.partNumber}-S/N:${labelInfo.serialNumber}^FS`;
    const notes = `^FO12,345^FB465,110^CF0,19^FD${(labelInfo.description || '').padEnd(2, ' ')}^FS`;
    const technician = `^FO490,356^FB105,110^CF0,15^FD${labelInfo.technician}^FS`;
    const specs = labelInfo.diagnostic.map((spec, idx) => {
      if (idx <= 10) {
        const row = 120;
        const col = 13;
        if (spec.substring(0, 6) !== 'failed') {
          return `^FO${col},${row + 20 * idx}^TBN,230,20^CF0,20^FD${spec}^FS`;
        }
        return (
          `^FO${col},${row + 20 * idx}^GB20,20,15,B,2^FS` +
          `^FO${col + 5},${row + 20 * idx}^FR^AC^FDF^FS` +
          `^FO${col + 22},${row + 20 * idx}^TBN,230,20^CF0,20^FD${spec.substring(6)}^FS`
        );
      }
      const row = 120;
      const col = 247;
      if (spec.substring(0, 6) !== 'failed') {
        return `^FO${col},${row + 20 * (idx - 11)}^TBN,230,20^CF0,20^FD${spec}^FS`;
      }
      return (
        `^FO${col},${row + 20 * (idx - 11)}^GB20,20,15,B,2^FS` +
        `^FO${col + 5},${row + 20 * (idx - 11)}^FR^AC^FDF^FS` +
        `^FO${col + 22},${row + 20 * (idx - 11)}^TBN,230,20^CF0,20^FD${spec.substring(6)}^FS`
      );
    });
    const endOfLabel = `^XZ`;
    return labelSkeleton + specs.join('') + partNumber + serialNumber + storageRam + cpuOs + grade + qrCode + notes + technician + endOfLabel;
  }

  // Print label handler
  const onPrintLabel = async (values) => {
    setLoading(true);
    setPreviewImg(null);
    try {
      let serialNumberInput = values.serialNumberInput.trim();
      if (serialNumberInput.substring(0, 4) === 'P/N:') {
        serialNumberInput = serialNumberInput.substring(serialNumberInput.indexOf('S/N:') + 4);
      }
      // Check if serial exists
      const inventoryItemsData = await api.post('inventory_items/showBySerialNumber', { serial_number: serialNumberInput });
      if (!inventoryItemsData.data) {
        message.error('Item not registered for this serial number');
        setLoading(false);
        return;
      }
      // Get label info
      const labelData = await getLabelInfo(serialNumberInput);
      if (!labelData) {
        setLoading(false);
        return;
      }
      // Build ZPL
      const zpl = buildZPL(labelData);
      const { selectedPrinterIp } = values;
      if (!selectedPrinterIp) {
        message.error('Please select a printer');
        setLoading(false);
        return;
      }
      persistSelectedPrinter(selectedPrinterIp);
      // Print to printer
      await fetch(`http://${selectedPrinterIp}/pstprnt`, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body: zpl,
      });
      // Update asset diagnostic
      const assetDiagnosticData = await api.post('assetDiagnostic/showBySerialNumber', { serialNumber: serialNumberInput });
      // Fix: extract the inventory item's ID from the earlier fetch, not from a null variable
      const existingInventoryItemsId = inventoryItemsData.data?.id || null;
      let inventoryItemsId = null;
      if (assetDiagnosticData.data !== null) {
        inventoryItemsId = assetDiagnosticData.data.id;
      } else {
        const aDInfo = await api.post('assetDiagnostic', {
          serial_number: serialNumberInput,
          // user_id: user.id, // user id not available in this context
          inventory_items_id: existingInventoryItemsId,
        });
        inventoryItemsId = aDInfo.data.id;
      }
      try {
        await api.put('inventory_items', {
          items: [
            {
              serial_number: serialNumberInput,
              asset_diagnostic_id: inventoryItemsId,
            },
          ],
        });
      } catch (error) {
        message.warning('The label was printed, but could not update system');
      }
      message.success('Label sent to printer');
      form.resetFields(['serialNumberInput']);
      const input = document.querySelector('input[id="serialNumberInput"]');
      if (input) input.focus();
    } catch (err) {
      message.error('Error printing label');
    } finally {
      setLoading(false);
    }
  };

  // Preview handler
  const onPreview = async (values) => {
    setPreviewLoading(true);
    setPreviewImg(null);
    try {
      let serialNumberInput = values.serialNumberInput.trim();
      if (serialNumberInput.substring(0, 4) === 'P/N:') {
        serialNumberInput = serialNumberInput.substring(serialNumberInput.indexOf('S/N:') + 4);
      }
      const labelData = await getLabelInfo(serialNumberInput);
      if (!labelData) {
        setPreviewLoading(false);
        return;
      }
      const zpl = buildZPL(labelData);
      // Labelary API expects ZPL in body
      const resp = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/3x2/0/', {
        method: 'POST',
        headers: {
          Accept: 'image/png',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: zpl,
      });
      if (!resp.ok) throw new Error('Labelary error');
      const blob = await resp.blob();
      setPreviewImg(URL.createObjectURL(blob));
      // message.success('Preview generated successfully');
    } catch (err) {
      message.error('Error generating preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Container>
      <Row justify="left" style={{ width: '100%' }}>
        <Col xs={24} sm={20} md={16} lg={12} xl={9}>
          <StyledCard>
            <TitleBar>
              <Title level={3} style={{ marginBottom: 0, fontWeight: 700, color: '#222' }}>
                Print Label
              </Title>
              <SubTitle>Generate and print asset labels with ease</SubTitle>
            </TitleBar>
            <Divider style={{ margin: '0 0 24px 0' }} />
            <Form form={form} onFinish={onPrintLabel} layout="vertical" autoComplete="off">
              <StyledFormItem name="selectedPrinterIp" label={<FormLabel>Printer</FormLabel>} rules={[{ required: true, message: 'Please select a printer' }]}>
                <Select
                  placeholder="Select a printer"
                  onChange={persistSelectedPrinter}
                  options={printers.map((printer) => ({
                    value: printer.value_param,
                    label: `${printer.key_param || 'Printer'} - ${printer.value_param}`,
                  }))}
                  size="large"
                  showSearch
                  optionFilterProp="label"
                />
              </StyledFormItem>
              <StyledFormItem
                name="serialNumberInput"
                label={<FormLabel>Serial Number</FormLabel>}
                rules={[{ required: true, message: 'Please enter a serial number' }]}
              >
                <Input autoFocus placeholder="Enter Serial Number" size="large" id="serialNumberInput" />
              </StyledFormItem>
              <ButtonGroup>
                <Button type="primary" htmlType="submit" loading={loading} size="large" style={{ flex: 1 }}>
                  Print Label
                </Button>
                <Button
                  type="default"
                  htmlType="button"
                  loading={previewLoading}
                  size="large"
                  style={{ flex: 1 }}
                  onClick={() => {
                    form
                      .validateFields(['serialNumberInput'])
                      .then(onPreview)
                      .catch(() => {});
                  }}
                >
                  Preview
                </Button>
              </ButtonGroup>
            </Form>
            <PreviewContainer>
              {previewLoading && <Spin />}
              {previewImg && <PreviewImage src={previewImg} alt="Label Preview" />}
            </PreviewContainer>
          </StyledCard>
        </Col>
      </Row>
    </Container>
  );
}
